import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Box, Grid, IconButton, Tooltip } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import {
  RMSTextField,
  RMSSelect,
  RMSDatePicker,
  RMSCheckbox,
} from '../../../shared/components/form-controls';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import addPersonSchema from '../../schema/addPersonSchema';
import {
  DEFAULT_VALUES,
  CONTACT_METHOD_TYPE,
  DICT_SELECT_PROPS,
  NAME_SNAPSHOT_FIELDS,
} from '../../constants/addPersonFormConstants';
import { buildPersonPayload, buildEditPersonPayload } from '../../utility/addPersonHelper';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import SearchAddressAutoComplete from '../../../shared/components/modules/SearchAddressAutoComplete';
import SectionLabel from './SectionLabel';
import AddIcon from '@material-ui/icons/Add';
import CityManagementDialog from './CityManagementDialog';
import AddAddressDialog from './AddAddressDialog';
import PersonNameDialog from './PersonNameDialog';
import { createPerson, editPerson, getPersonDetails } from '../../services/userService';

const PersonFormFields = forwardRef(
  ({ open = true, personId = null, onSaved, onLoadingChange, onStateChange }, ref) => {
    const dispatch = useDispatch();
    const cityDictPerms = useSelector((state) => state.permissions?.dictionary?.codeCities);
    const [submitting, setSubmitting] = useState(false);
    const [loadingPerson, setLoadingPerson] = useState(false);
    const [cityManagementDialogOpen, setCityManagementDialogOpen] = useState(false);
    const [addAddressDialogOpen, setAddAddressDialogOpen] = useState(false);
    const [nameChangeDialogOpen, setNameChangeDialogOpen] = useState(false);
    const [pendingSubmitData, setPendingSubmitData] = useState(null);
    const nameSnapshotRef = useRef(null);

    const isEditMode = Boolean(personId);
    const personPerms = getFullPermissions('globals', 'People', 'any');
    const canSavePerson = isEditMode ? personPerms.Edit : personPerms.Create;
    const isViewOnly = isEditMode && !personPerms.Edit;
    const showCityAddIcon = Boolean(
      cityDictPerms?.Create || cityDictPerms?.Edit || cityDictPerms?.Delete
    );

    const methods = useForm({
      resolver: CreateFormResolver(addPersonSchema),
      mode: 'onSubmit',
      defaultValues: DEFAULT_VALUES,
      disabled: isViewOnly,
    });

    const { control, setValue } = methods;

    const { isDirty } = useFormState({ control });
    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;

    const {
      prefixOptions,
      suffixOptions,
      raceOptions,
      sexOptions,
      cityOptions,
      stateOptions,
      countyOptions,
      countryOptions,
      deathCauseOptions,
      residentOptions,
      citizenshipOptions,
      nationalityOptions,
      gangOptions,
      maritalStatusOptions,
      ethnicityOptions,
      eyeColorOptions,
      handedOptions,
      bloodTypeOptions,
      hairColorOptions,
      hairLengthOptions,
      hairStyleOptions,
      contactTypeOptions,
      languageOptions,
      dangerousTypeOptions,
    } = useUserDictionaryOptions();

    const isDeceased = useWatch({ control, name: 'Deceased' });
    const contactMethodType = useWatch({ control, name: 'ContactMethodType' });
    const isDangerous = useWatch({ control, name: 'Dangerous' });
    const ptsAddressID = useWatch({ control, name: 'ptsAddressID' });
    const [recordCreated, recordCreatedBy, recordUpdated, recordUpdatedBy] = useWatch({
      control,
      name: ['Created', 'CreatedBy', 'Updated', 'UpdatedBy'],
    });

    const contactMethodTypeOptions = useMemo(
      () => [
        { Code: CONTACT_METHOD_TYPE.PHONE, Description: CONTACT_METHOD_TYPE.PHONE },
        { Code: CONTACT_METHOD_TYPE.EMAIL, Description: CONTACT_METHOD_TYPE.EMAIL },
      ],
      []
    );

    const birthCityOptions = useMemo(
      () =>
        cityOptions.map((city) => ({
          ...city,
          Description: city.State
            ? `${city.CityDescription} - ${city.State}`
            : city.CityDescription,
        })),
      [cityOptions]
    );

    const handleAddressSelection = useCallback(
      (address) => {
        setValue('ptsAddressID', address?.ptsAddressID ?? null, { shouldDirty: true });
        setValue('AddressSearch', address?.ptsAddressID ?? null, { shouldDirty: true });
      },
      [setValue]
    );

    const handleBirthCityCreated = useCallback(
      (city) => {
        setValue('ptsBirthCityID', city?.ptsCityID ?? null, {
          shouldDirty: true,
          shouldValidate: true,
        });
      },
      [setValue]
    );

    const hasNameChanged = useCallback((data) => {
      if (!nameSnapshotRef.current) return false;
      return NAME_SNAPSHOT_FIELDS.some(
        (field) => (data[field] || '') !== nameSnapshotRef.current[field]
      );
    }, []);

    const performSave = useCallback(
      async (data) => {
        await executeApiAction({
          setLoading: setSubmitting,
          dispatch,
          action: async () => {
            let savedPersonId = personId;

            if (isEditMode) {
              await editPerson(personId, buildEditPersonPayload(data));
            } else {
              const created = await createPerson(buildPersonPayload(data));
              savedPersonId = created.ptsPersonID;
            }

            const [personDetails] = await getPersonDetails(savedPersonId);
            return personDetails || { ptsPersonID: savedPersonId };
          },
          onSuccess: (person) => {
            dispatch(
              notify(
                isEditMode ? 'Person updated successfully' : 'Person created successfully',
                'success'
              )
            );

            if (person) {
              methods.reset({ ...DEFAULT_VALUES, ...person });
              nameSnapshotRef.current = NAME_SNAPSHOT_FIELDS.reduce(
                (snapshot, field) => ({ ...snapshot, [field]: person[field] || '' }),
                {}
              );
            }

            onSaved?.(person, { isEditMode });
          },
        });
      },
      [personId, isEditMode, dispatch, methods, onSaved]
    );

    const onSubmit = useCallback(
      async (data) => {
        if (isEditMode && hasNameChanged(data)) {
          setPendingSubmitData(data);
          setNameChangeDialogOpen(true);
          return;
        }

        await performSave(data);
      },
      [isEditMode, hasNameChanged, performSave]
    );

    const handleSave = useCallback((event) => methods.handleSubmit(onSubmit)(event), [
      methods,
      onSubmit,
    ]);

    const handleNameChangeSaved = useCallback(async () => {
      setNameChangeDialogOpen(false);
      const data = pendingSubmitData;
      setPendingSubmitData(null);
      if (data) await performSave(data);
    }, [pendingSubmitData, performSave]);

    const handleNameChangeCancel = useCallback(() => {
      setNameChangeDialogOpen(false);
      setPendingSubmitData(null);
    }, []);

    useEffect(() => {
      onLoadingChange?.(submitting || loadingPerson);
    }, [submitting, loadingPerson, onLoadingChange]);

    useEffect(() => {
      if (!open || !personId) return;

      executeApiAction({
        setLoading: setLoadingPerson,
        dispatch,
        action: () => getPersonDetails(personId),
        onSuccess: ([person]) => {
          if (person) {
            methods.reset({ ...DEFAULT_VALUES, ...person });
            nameSnapshotRef.current = NAME_SNAPSHOT_FIELDS.reduce(
              (snapshot, field) => ({ ...snapshot, [field]: person[field] || '' }),
              {}
            );
          }
        },
      });
    }, [open, personId, dispatch, methods]);

    useImperativeHandle(
      ref,
      () => ({
        submit: handleSave,
        resetForm: () => methods.reset(DEFAULT_VALUES),
        isDirty: () => isDirtyRef.current,
      }),
      [handleSave, methods]
    );

    useEffect(() => {
      onStateChange?.({
        canSave: canSavePerson,
        submitting,
        isEditMode,
        recordCreated,
        recordCreatedBy,
        recordUpdated,
        recordUpdatedBy,
      });
    }, [
      onStateChange,
      canSavePerson,
      submitting,
      isEditMode,
      recordCreated,
      recordCreatedBy,
      recordUpdated,
      recordUpdatedBy,
    ]);

    return (
      <>
        <FormProvider {...methods}>
          <Box className="rms-dialog-section">
            <SectionLabel>Name</SectionLabel>
            <Grid container spacing={2}>
              <Grid item xs={2}>
                <RMSSelect
                  name="Prefix"
                  label="Prefix"
                  options={prefixOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={3}>
                <RMSTextField name="FirstName" label="First Name" />
              </Grid>
              <Grid item xs={3}>
                <RMSTextField name="MiddleName" label="Middle Name" />
              </Grid>
              <Grid item xs={2}>
                <RMSTextField name="LastName" label="Last Name" />
              </Grid>
              <Grid item xs={2}>
                <RMSSelect
                  name="Suffix"
                  label="Suffix"
                  options={suffixOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={3}>
                <RMSTextField name="NickName" label="Nick Name" />
              </Grid>
            </Grid>
          </Box>

          <Box className="rms-dialog-section">
            <SectionLabel>General Details</SectionLabel>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <RMSTextField name="GeneralAppearance" label="General Appearance" />
              </Grid>
              <Grid item xs={2}>
                <RMSSelect name="Race" label="Race" options={raceOptions} {...DICT_SELECT_PROPS} />
              </Grid>
              <Grid item xs={2}>
                <RMSSelect name="Sex" label="Sex" options={sexOptions} {...DICT_SELECT_PROPS} />
              </Grid>
              <Grid item xs={2}>
                <RMSTextField
                  name="Height"
                  label="Height"
                  type="number"
                  inputProps={{ maxLength: 3 }}
                  rightContent={
                    <Tooltip title="Enter feet then inches as one number. E.g. 501 = 5ft 1in, 611 = 6ft 11in">
                      <IconButton size="small">
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                />
              </Grid>
              <Grid item xs={2}>
                <RMSTextField
                  name="Weight"
                  label="Weight"
                  type="number"
                  inputProps={{ maxLength: 3 }}
                />
              </Grid>
            </Grid>
          </Box>

          <Box className="rms-dialog-section">
            <SectionLabel>Age</SectionLabel>
            <Grid container spacing={2}>
              <Grid item xs={2}>
                <RMSDatePicker name="Born" label="Birth Date" maxDate={new Date()} />
              </Grid>
              <Grid item xs={3}>
                <RMSSelect
                  name="ptsBirthCityID"
                  label="Birth City"
                  options={birthCityOptions}
                  {...DICT_SELECT_PROPS}
                  showLabelOnly
                  clearable
                  prefixIcon={showCityAddIcon ? <AddIcon fontSize="small" /> : undefined}
                  onPrefixIconClick={
                    showCityAddIcon ? () => setCityManagementDialogOpen(true) : undefined
                  }
                />
              </Grid>
              <Grid item xs={2}>
                <RMSSelect
                  name="BirthState"
                  label="Birth State"
                  options={stateOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={2}>
                <RMSSelect
                  name="BirthCounty"
                  label="Birth County"
                  options={countyOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={3}>
                <RMSSelect
                  name="BirthCountry"
                  label="Birth Country"
                  options={countryOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={2}>
                <RMSCheckbox name="Deceased" label="Deceased" />
              </Grid>
              <Grid item xs={2}>
                <RMSDatePicker name="Died" label="Deceased Date" disabled={!isDeceased} />
              </Grid>
              <Grid item xs={3}>
                <RMSSelect
                  name="CauseOfDeath"
                  label="Cause of Death"
                  options={deathCauseOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
            </Grid>
          </Box>

          <Box className="rms-dialog-section">
            <SectionLabel>Citizenship</SectionLabel>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <RMSSelect
                  name="LocalResident"
                  label="Local Resident"
                  options={residentOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={2}>
                <RMSCheckbox name="IsUSCitizen" label="US Citizen" />
              </Grid>
              <Grid item xs={3}>
                <RMSSelect
                  name="CurrentCitizenship"
                  label="Current Citizenship"
                  options={citizenshipOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={2}>
                <RMSSelect
                  name="BirthCitizenship"
                  label="Birth Citizenship"
                  options={citizenshipOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={2}>
                <RMSSelect
                  name="BirthNationality"
                  label="Birth Nationality"
                  options={nationalityOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
            </Grid>
          </Box>

          <Box className="rms-dialog-section">
            <SectionLabel>Advanced Details</SectionLabel>
            <Grid container spacing={2}>
              <Grid item xs={2}>
                <RMSSelect name="Gang" label="Gang" options={gangOptions} {...DICT_SELECT_PROPS} />
              </Grid>
              <Grid item xs={2}>
                <RMSSelect
                  name="MaritalStatus"
                  label="Marital Status"
                  options={maritalStatusOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={2}>
                <RMSTextField name="EducationLevel" label="Education Level" />
              </Grid>
              <Grid item xs={2}>
                <RMSTextField name="Allergies" label="Allergies" />
              </Grid>
              <Grid item xs={2}>
                <RMSTextField name="DNA" label="DNA" />
              </Grid>
              <Grid item xs={12}>
                <RMSTextField name="Notes" label="Person Notes" multiline rows={3} />
              </Grid>
            </Grid>
          </Box>

          <Box className="rms-dialog-section">
            <SectionLabel>Features</SectionLabel>
            <Grid container spacing={2}>
              <Grid item xs={2}>
                <RMSSelect
                  name="Ethnicity"
                  label="Ethnicity"
                  options={ethnicityOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={2}>
                <RMSSelect
                  name="EyeColor"
                  label="Eye Color"
                  options={eyeColorOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={2}>
                <RMSSelect
                  name="Handed"
                  label="Handed"
                  options={handedOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={2}>
                <RMSSelect
                  name="BloodType"
                  label="Blood Type"
                  options={bloodTypeOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={2}>
                <RMSSelect
                  name="HairColor"
                  label="Hair Color"
                  options={hairColorOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={2}>
                <RMSSelect
                  name="HairLength"
                  label="Hair Length"
                  options={hairLengthOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={2}>
                <RMSSelect
                  name="HairStyle"
                  label="Hair Style"
                  options={hairStyleOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
            </Grid>
          </Box>

          {!isEditMode && (
            <Box className="rms-dialog-section">
              <SectionLabel>Credentials</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <RMSTextField name="SSN" label="SSN" />
                </Grid>
                <Grid item xs={4}>
                  <RMSTextField name="OLN" label="OLN" />
                </Grid>
                <Grid item xs={4}>
                  <RMSSelect
                    name="OLNState"
                    label="State"
                    options={stateOptions}
                    {...DICT_SELECT_PROPS}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {!isEditMode && (
            <Box className="rms-dialog-section">
              <SectionLabel>Contact</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <SearchAddressAutoComplete
                    name="AddressSearch"
                    label="Address Lookup"
                    defaultAddressId={ptsAddressID}
                    onSelectionChange={handleAddressSelection}
                    onAddIconClick={() => setAddAddressDialogOpen(true)}
                    showDetailsIcon={false}
                  />
                </Grid>
                <Grid item xs={2}>
                  <RMSCheckbox name="IsPrimaryAddress" label="Primary" />
                </Grid>
                <Grid item xs={3}>
                  <RMSSelect
                    name="ContactMethodType"
                    label="Method Type"
                    options={contactMethodTypeOptions}
                    {...DICT_SELECT_PROPS}
                  />
                </Grid>
                <Grid item xs={3}>
                  <RMSSelect
                    name="ContactType"
                    label="Contact Type"
                    options={contactTypeOptions}
                    disabled={!contactMethodType}
                    {...DICT_SELECT_PROPS}
                  />
                </Grid>
                <Grid item xs={4}>
                  <RMSTextField name="ContactInfo" label="Info" disabled={!contactMethodType} />
                </Grid>
                <Grid item xs={2}>
                  <RMSCheckbox name="IsDefaultContact" label="Default" />
                </Grid>
                <Grid item xs={10}>
                  <RMSTextField name="ContactNotes" label="Notes" />
                </Grid>
              </Grid>
            </Box>
          )}

          <Box className="rms-dialog-section rms-dialog-section--last">
            <SectionLabel>Flags</SectionLabel>
            <Grid container spacing={2}>
              <Grid item xs={2}>
                <RMSCheckbox name="PrintsAvailable" label="Finger Print Available" />
              </Grid>
              <Grid item xs={2}>
                <RMSCheckbox name="OverrideJuvenile" label="Override Juvenile" />
              </Grid>
              <Grid item xs={2}>
                <RMSCheckbox name="PublicOfficial" label="Public Official" />
              </Grid>
              <Grid item xs={2}>
                <RMSCheckbox name="EnglishLangIndicator" label="English Speaking" />
              </Grid>
              <Grid item xs={4}>
                <RMSSelect
                  name="PrimaryLanguage"
                  label="Primary Language"
                  options={languageOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={2}>
                <RMSCheckbox name="HasDigitalImage" label="Digital Image" />
              </Grid>
              <Grid item xs={2}>
                <RMSCheckbox name="Incarcerated" label="In Jail" />
              </Grid>
              <Grid item xs={2}>
                <RMSCheckbox name="ConcealedWeaponPermit" label="Carry Weapon" />
              </Grid>
              <Grid item xs={2}>
                <RMSCheckbox name="Dangerous" label="Dangerous" />
              </Grid>
              <Grid item xs={4}>
                <RMSSelect
                  name="WhyDangerous"
                  label="Why Dangerous"
                  options={dangerousTypeOptions}
                  disabled={!isDangerous}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={2}>
                <RMSCheckbox name="IsOrganDonor" label="Organ Donor" />
              </Grid>
              <Grid item xs={2}>
                <RMSCheckbox name="ExFelon" label="Ex Felon" />
              </Grid>
              <Grid item xs={2}>
                <RMSCheckbox name="ProtectiveOrder" label="Protective Order" />
              </Grid>
              <Grid item xs={2}>
                <RMSCheckbox name="SeniorCitizen" label="Senior Citizen" />
              </Grid>
              <Grid item xs={2}>
                <RMSCheckbox name="Officer" label="Officer" />
              </Grid>
              <Grid item xs={2}>
                <RMSCheckbox name="SexOffender" label="Sex Offender" />
              </Grid>
              <Grid item xs={2}>
                <RMSCheckbox name="Juvenile" label="Juvenile" />
              </Grid>
              <Grid item xs={2}>
                <RMSCheckbox name="Military" label="Military" />
              </Grid>
              <Grid item xs={2}>
                <RMSCheckbox name="Probation" label="Probation" />
              </Grid>
            </Grid>
          </Box>
        </FormProvider>

        {cityManagementDialogOpen && (
          <CityManagementDialog
            open={cityManagementDialogOpen}
            onClose={() => setCityManagementDialogOpen(false)}
            onCityCreated={handleBirthCityCreated}
          />
        )}

        {addAddressDialogOpen && (
          <AddAddressDialog
            open={addAddressDialogOpen}
            onClose={() => setAddAddressDialogOpen(false)}
            onAddressCreated={handleAddressSelection}
          />
        )}

        {nameChangeDialogOpen && (
          <PersonNameDialog
            open={nameChangeDialogOpen}
            personId={personId}
            nameChangeSnapshot={nameSnapshotRef.current}
            onClose={handleNameChangeCancel}
            onSaved={handleNameChangeSaved}
          />
        )}
      </>
    );
  }
);

PersonFormFields.displayName = 'PersonFormFields';

export default PersonFormFields;
